import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase, isSupabaseAvailable } from '../lib/supabase';

/**
 * Version der Einführungstour. Hochzählen, wenn die Tour inhaltlich erweitert
 * wurde und alle sie noch einmal sehen sollen — der gespeicherte Wert pro
 * Person ist dann kleiner als diese Zahl und die Tour startet erneut.
 */
export const TOUR_VERSION = 1;

/**
 * Gemerkt wird der Abschluss in `agent_settings.prefs.tour_version` — dieselbe
 * Zeile, in der auch die Dialer-Einstellungen der Person liegen. Damit hängt
 * die Tour am Konto und nicht am Browser: wer sich zu Hause neu anmeldet,
 * bekommt sie nicht ein zweites Mal. Zusätzlich liegt der Wert im Browser
 * (persist), damit ein kurzer Datenbank-Aussetzer die Tour nicht wieder
 * hervorholt.
 */
interface TourState {
  /** Zuletzt abgeschlossene Version dieser Person (0 = noch nie gesehen) */
  seenVersion: number;
  /** Aus der Datenbank gelesen? Vorher startet nichts von allein. */
  isLoaded: boolean;
  /** Läuft die Tour gerade? */
  isRunning: boolean;
  /** Aktueller Schritt (Index in der zusammengestellten Schrittliste) */
  stepIndex: number;
  /** Seite, auf der die Tour gestartet wurde — dorthin geht es am Ende zurück */
  returnTo: string | null;
  /** Konto, zu dem der gemerkte Stand gehört */
  userId: string | null;

  /** Stand aus der Datenbank laden (nach dem Anmelden) */
  initialize: (userId: string) => Promise<void>;
  /** Soll die Tour von allein starten? */
  shouldAutoStart: () => boolean;
  start: (returnTo?: string | null) => void;
  goTo: (index: number) => void;
  next: (totalSteps: number) => void;
  back: () => void;
  /** Tour beenden (durchgeklickt oder übersprungen) und Abschluss merken */
  finish: () => Promise<void>;
  /** Beim Abmelden zurücksetzen */
  reset: () => void;
}

type Prefs = Record<string, unknown>;

const readPrefs = async (userId: string): Promise<Prefs> => {
  const { data, error } = await supabase
    .from('agent_settings')
    .select('prefs')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return ((data?.prefs as Prefs | null) ?? {}) as Prefs;
};

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      seenVersion: 0,
      isLoaded: false,
      isRunning: false,
      stepIndex: 0,
      returnTo: null,
      userId: null,

      initialize: async (userId) => {
        // Kontowechsel im selben Browser: der gemerkte Stand gehört zur alten
        // Person und darf die Tour für die neue nicht unterdrücken.
        if (get().userId && get().userId !== userId) {
          set({ seenVersion: 0 });
        }
        set({ userId });

        if (!isSupabaseAvailable) {
          // Ohne Datenbank ließe sich der Abschluss nicht speichern — die Tour
          // käme bei jedem Aufruf wieder. Dann lieber gar nicht starten.
          set({ isLoaded: true, seenVersion: TOUR_VERSION });
          return;
        }
        try {
          const prefs = await readPrefs(userId);
          const stored = Number(prefs.tour_version ?? 0);
          set({ seenVersion: Number.isFinite(stored) ? stored : 0, isLoaded: true });
        } catch (error) {
          // Im Zweifel nicht starten: eine Tour, die wegen eines Ladefehlers
          // bei jedem Login aufpoppt, nervt mehr als eine ausgelassene Tour.
          console.error('Tour-Stand konnte nicht geladen werden:', error);
          set({ isLoaded: true, seenVersion: TOUR_VERSION });
        }
      },

      shouldAutoStart: () => {
        const { isLoaded, seenVersion, isRunning } = get();
        return isLoaded && !isRunning && seenVersion < TOUR_VERSION;
      },

      start: (returnTo = null) => set({ isRunning: true, stepIndex: 0, returnTo }),

      goTo: (index) => set({ stepIndex: Math.max(0, index) }),

      next: (totalSteps) => {
        const { stepIndex } = get();
        if (stepIndex + 1 >= totalSteps) {
          void get().finish();
          return;
        }
        set({ stepIndex: stepIndex + 1 });
      },

      back: () => set((s) => ({ stepIndex: Math.max(0, s.stepIndex - 1) })),

      finish: async () => {
        const userId = get().userId;
        set({ isRunning: false, stepIndex: 0, seenVersion: TOUR_VERSION });
        if (!isSupabaseAvailable || !userId) return;
        try {
          // Lesen-Ändern-Schreiben, damit andere Einträge in `prefs` erhalten
          // bleiben. Die übrigen Spalten fasst der Upsert nicht an.
          const prefs = await readPrefs(userId);
          const { error } = await supabase.from('agent_settings').upsert(
            {
              user_id: userId,
              prefs: { ...prefs, tour_version: TOUR_VERSION },
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id' },
          );
          if (error) throw error;
        } catch (error) {
          // Lokal ist die Tour beendet; der gemerkte Browser-Stand trägt so
          // lange, bis das Speichern wieder klappt.
          console.error('Tour-Abschluss konnte nicht gespeichert werden:', error);
        }
      },

      reset: () =>
        set({ isLoaded: false, isRunning: false, stepIndex: 0, returnTo: null }),
    }),
    {
      name: 'cc-tour',
      // isRunning/stepIndex sind reiner Sitzungszustand: nach einem Neuladen
      // soll die Tour nicht mitten in Schritt 12 weiterlaufen.
      partialize: (s) => ({ seenVersion: s.seenVersion, userId: s.userId }),
    },
  ),
);
